
import { Midi } from '@tonejs/midi';
import { MidiData, MidiNote } from '../types';

export class MidiParser {
  public static parseMidiBuffer(arrayBuffer: ArrayBuffer, name: string): MidiData {
    const midi = new Midi(arrayBuffer);
    
    const notes: MidiNote[] = [];
    const tracksInfo: { index: number, name: string }[] = [];
    midi.tracks.forEach((track, trackIndex) => {
      tracksInfo.push({ index: trackIndex, name: track.name || `Track ${trackIndex + 1}` });
      track.notes.forEach(note => {
        notes.push({
          name: note.name,
          midi: note.midi,
          time: note.time,
          duration: note.duration,
          velocity: note.velocity,
          track: trackIndex,
          channel: (note as any).channel
        });
      });
    });

    notes.sort((a, b) => a.time - b.time);

    return {
      notes,
      duration: midi.duration,
      header: {
        name: midi.name || name,
        ppq: midi.header.ppq,
        tempos: midi.header.tempos.map(t => ({ bpm: t.bpm, time: t.ticks })),
        timeSignatures: midi.header.timeSignatures.map(ts => ({ timeSignature: ts.timeSignature, time: ts.ticks })),
        tracks: tracksInfo
      }
    };
  }

  public static async parseMidiFile(file: File): Promise<MidiData> {
    const arrayBuffer = await file.arrayBuffer();
    return this.parseMidiBuffer(arrayBuffer, file.name);
  }

  public static parseMidiBase64(base64: string, name: string = 'Pasted MIDI'): MidiData {
    const binaryString = atob(base64.split(',')[1] || base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return this.parseMidiBuffer(bytes.buffer, name);
  }

  public static getMidiTracks(midiData: MidiData): { index: number, name: string }[] {
    if (midiData.header.tracks) return midiData.header.tracks;
    
    const tracks: { index: number, name: string }[] = [];
    const seen = new Set<number>();
    
    midiData.notes.forEach(note => {
      if (note.track !== undefined && !seen.has(note.track)) {
        seen.add(note.track);
        tracks.push({ index: note.track, name: `Track ${note.track + 1}` });
      }
    });
    
    return tracks;
  }

  public static parseOnlineSequencerString(data: string): MidiData {
    const noteMap: Record<string, number> = {
        'C': 0, 'C#': 1, 'Db': 1, 'D': 2, 'D#': 3, 'Eb': 3, 'E': 4, 'F': 5, 'F#': 6, 'Gb': 6, 'G': 7, 'G#': 8, 'Ab': 8, 'A': 9, 'A#': 10, 'Bb': 10, 'B': 11
    };

    const noteToMidi = (note: string): number => {
        // Handle MIDI numbers directly
        if (/^\d+$/.test(note)) return parseInt(note);
        
        const match = note.match(/^([A-G][b#]?)(\d+)$/i);
        if (!match) return 60;
        const name = match[1].toUpperCase();
        const octave = parseInt(match[2]);
        return (octave + 1) * 12 + (noteMap[name] || 0);
    };

    // Format can be "Online Sequencer:BPM:notes" or "Online Sequencer:ID:notes"
    // We want to skip the "Online Sequencer:XXXX:" part
    let workingData = data.trim();
    let bpm = 120;

    if (workingData.startsWith('Online Sequencer:')) {
        // Find the index of the second colon
        const firstColon = workingData.indexOf(':', 0);
        const secondColon = workingData.indexOf(':', firstColon + 1);
        
        if (secondColon !== -1) {
            const potentialBpm = workingData.substring(firstColon + 1, secondColon);
            const val = parseInt(potentialBpm);
            if (!isNaN(val) && val >= 20 && val <= 500) bpm = val;
            workingData = workingData.substring(secondColon + 1);
        } else {
            workingData = workingData.replace('Online Sequencer:', '');
        }
    }

    const parts = workingData.split(';');
    const notes: MidiNote[] = [];
    
    for (let i = 0; i < parts.length; i++) {
        const part = parts[i].trim();
        if (!part) continue;

        // Try colon first, then space (Online Sequencer uses both in different versions)
        let noteParts = part.includes(':') ? part.split(':') : part.split(/\s+/);
        
        // Expected format: [Type/ID, Time, Pitch, Length, Instrument] OR [Time, Pitch, Length, Instrument]
        // The user's format seems to be [Time, Pitch, Length, Instrument] (length 4)
        if (noteParts.length < 3) continue;

        let timeIdx = 0;
        let pitchIdx = 1;
        let lengthIdx = 2;
        let instrumentIdx = 3;

        // If it looks like it has a type prefix (usually when length >= 5)
        // Online Sequencer format (colon-separated): [Type, Time, Instrument, Pitch, Length]
        if (noteParts.length >= 5) {
            timeIdx = 1;
            instrumentIdx = 2;
            pitchIdx = 3;
            lengthIdx = 4;
        }

        if (noteParts.length <= Math.max(timeIdx, pitchIdx, lengthIdx)) continue;

        const time = parseFloat(noteParts[timeIdx]);
        const pitchStr = noteParts[pitchIdx];
        const length = parseFloat(noteParts[lengthIdx]) || 1;
        const instrumentId = noteParts[instrumentIdx] ? parseInt(noteParts[instrumentIdx]) : 0;

        if (isNaN(time)) continue;

        notes.push({
            midi: noteToMidi(pitchStr),
            name: pitchStr,
            time: (time / 4) * (60 / bpm),
            duration: (length / 4) * (60 / bpm),
            velocity: 0.8,
            track: instrumentId
        });
    }

    notes.sort((a, b) => a.time - b.time);

    return {
        notes,
        duration: notes.reduce((max, n) => Math.max(max, n.time + n.duration), 0),
        header: {
            name: 'Online Sequencer Paste',
            ppq: 192,
            tempos: [{ bpm, time: 0 }],
            timeSignatures: [{ timeSignature: [4, 4], time: 0 }],
            tracks: Array.from(new Set(notes.map(n => n.track))).map(t => ({ index: t!, name: `Instrument ${t}` }))
        }
    };
  }
}
